import { useEffect, useState } from "react";
import { CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ProductCategories } from "./ProductCategories";
import { errMsg } from "./kpiFormat";

const KINDS = [
  { v: "intro", label: "Intro offer" },
  { v: "recurring", label: "Recurring membership" },
  { v: "pack", label: "Qualifying pre-paid pack" },
  { v: "none", label: "Other / PAYG" },
];
const kindOf = (f) => (f.is_intro ? "intro" : f.is_recurring ? "recurring" : f.is_qualifying_pack ? "pack" : "none");
const NONE = "__none";

const DaysInput = ({ value, onChange, disabled, testId, placeholder }) => (
  <Input type="number" min={0} max={730} value={value ?? ""} placeholder={placeholder} disabled={disabled}
    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="h-8 w-20 font-mono-num" data-testid={testId} />
);

const ProductRow = ({ p, services, canEdit, onSaved }) => {
  const eff = p.effective;
  const [f, setF] = useState({
    kind: kindOf(eff), service_id: eff.service_id || NONE, intro_days: p.intro_days ?? null, rollover_days: p.rollover_days ?? null,
    purchase_window_days: p.purchase_window_days ?? null, excluded: !!p.excluded, activation_rule: p.activation_rule || "purchase",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/kpi/products/${encodeURIComponent(p.id)}`, {
        is_intro: f.kind === "intro", is_recurring: f.kind === "recurring", is_qualifying_pack: f.kind === "pack",
        service_id: f.service_id === NONE ? null : f.service_id, intro_days: f.intro_days, rollover_days: f.rollover_days,
        purchase_window_days: f.purchase_window_days, excluded: f.excluded, activation_rule: f.activation_rule, confirmed: true,
      });
      onSaved(r.data);
      toast.success(`${p.name} confirmed`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const tid = p.id.replace(/[^a-z0-9]+/gi, "-");
  return (
    <tr className="border-t border-zinc-100 align-middle" data-testid={`kpi-product-row-${tid}`}>
      <td className="py-2 pr-3">
        <div className="font-medium text-zinc-900">{p.name}</div>
        <div className="text-2xs text-zinc-500">{p.source}{p.price ? ` · $${p.price}` : ""}</div>
      </td>
      <td className="py-2 pr-3">
        <Select value={f.kind} onValueChange={(v) => set("kind", v)} disabled={!canEdit}>
          <SelectTrigger className="h-8 w-56" data-testid={`kpi-product-kind-${tid}`}><SelectValue /></SelectTrigger>
          <SelectContent>{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="py-2 pr-3">
        <Select value={f.service_id} disabled={!canEdit} onValueChange={(v) => {
          set("service_id", v);
          if (services.find((s) => s.id === v)?.kind === "category") set("kind", "none");
        }}>
          <SelectTrigger className="h-8 w-44" data-testid={`kpi-product-service-${tid}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Any service</SelectItem>
            <SelectGroup>
              <SelectLabel className="text-2xs uppercase tracking-[0.16em] text-zinc-400">Services</SelectLabel>
              {services.filter((s) => s.kind !== "category").map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectGroup>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel className="text-2xs uppercase tracking-[0.16em] text-zinc-400">Product categories</SelectLabel>
              {services.filter((s) => s.kind === "category" && (s.active !== false || s.id === f.service_id))
                .map((s) => <SelectItem key={s.id} value={s.id} data-testid={`kpi-product-service-option-${s.id}`}>{s.name}</SelectItem>)}
            </SelectGroup>
          </SelectContent>
        </Select>
      </td>
      <td className="py-2 pr-3">
        {f.kind === "intro" ? (
          <div className="flex items-center gap-1.5">
            <DaysInput value={f.intro_days} onChange={(v) => set("intro_days", v)} disabled={!canEdit} placeholder="28" testId={`kpi-product-intro-days-${tid}`} />
            <DaysInput value={f.rollover_days} onChange={(v) => set("rollover_days", v)} disabled={!canEdit} placeholder="7" testId={`kpi-product-rollover-days-${tid}`} />
            <Select value={f.activation_rule} onValueChange={(v) => set("activation_rule", v)} disabled={!canEdit}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid={`kpi-product-activation-${tid}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="purchase">From purchase</SelectItem>
                <SelectItem value="first_class">From first class</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : <span className="text-xs text-zinc-400">—</span>}
      </td>
      <td className="py-2 pr-3">
        <Switch checked={f.excluded} onCheckedChange={(v) => set("excluded", v)} disabled={!canEdit} data-testid={`kpi-product-excluded-${tid}`} />
      </td>
      <td className="py-2 pr-3">
        <span className={cn("text-2xs font-semibold uppercase tracking-[0.12em]", p.confirmed ? "text-emerald-700" : "text-amber-700")}
          data-testid={`kpi-product-state-${tid}`}>{p.confirmed ? "Confirmed" : "Suggested"}</span>
      </td>
      <td className="py-2 text-right">
        {canEdit && <Button size="sm" variant={p.confirmed ? "outline" : "default"} disabled={busy} onClick={save}
          data-testid={`kpi-product-save-${tid}`}>{p.confirmed ? "Save" : "Confirm"}</Button>}
      </td>
    </tr>
  );
};

export const ProductMapping = ({ canEdit, services, reloadServices }) => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/kpi/products").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const confirmAll = async () => {
    try {
      const r = await api.post("/kpi/products/confirm-suggestions");
      toast.success(`${r.data.confirmed} product(s) confirmed`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pending = rows?.filter((p) => !p.confirmed).length || 0;
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4" data-testid="kpi-product-mapping">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Products & offers</h3>
          <p className="text-sm text-zinc-500 max-w-2xl">Tell the report what each Acuity, Stripe or imported product is. Suggestions come from the
            product name — nothing counts as confirmed until you save it. Retail items (e.g. Sock Aid) go under a product category; their
            sales still count in revenue. Intro rows take the intro length and rollover window (days).</p>
        </div>
        {canEdit && pending > 0 && <Button size="sm" variant="outline" onClick={confirmAll} data-testid="kpi-products-confirm-all-btn">
          <CheckCheck className="h-4 w-4 mr-1.5" />Confirm {pending} suggestion(s)</Button>}
      </div>
      <ProductCategories services={services} canEdit={canEdit} onChange={reloadServices} />
      {!rows ? <div className="h-24 animate-pulse rounded bg-zinc-100" /> : rows.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="kpi-products-empty">No products yet — run a sync or import transactions first.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-2xs uppercase tracking-[0.18em] text-zinc-500">
              <th className="pb-2">Product</th><th className="pb-2">Counts as</th><th className="pb-2">Service</th>
              <th className="pb-2">Intro / rollover days</th><th className="pb-2">Exclude</th><th className="pb-2">State</th><th /></tr></thead>
            <tbody>{rows.map((p) => <ProductRow key={`${p.id}-${p.config_version || 0}`} p={p} services={services} canEdit={canEdit}
              onSaved={(np) => setRows((rs) => rs.map((x) => x.id === np.id ? { ...x, ...np, effective: { ...x.effective, ...np.flags } } : x))} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
};
